import { useContext } from "react";
import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";
import {
    Button,
    FilterClear,
    FilterGroup,
    HeaderFilter,
    Input,
    OptionType,
    StatusFilter,
    useDebounced,
    useGetFilterPreferencesQuery,
    useOutsideClick,
    UserAccountActiveStatus,
    useTitle,
    useUpdateFilterPreferences,
} from "modules/common";
import { mapToFilterName, OuterModulesFilter } from "modules/common/utils/filters";
import { AdministrationFilters } from "modules/dashboard";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SortingRule } from "react-table";
import { AppRoutes } from "routing";
import { ReactComponent as SearchIcon } from "assets/icons/search.svg";
import { useOuterModules, OuterModuleDetailsModal, OuterModulesTable } from "modules/outerModules";
import { OuterModule } from "services";
import { AccessibilityContext } from "./AccessibilityContext";

const INPUT_DEBOUNCE_TIME = 500;

// map filter names
const STATUS_FILTER_NAME = mapToFilterName<OuterModulesFilter>(
    AdministrationFilters.name,
    "statusFilter"
);

export const ModulesAdministration = () => {
    useTitle("Administracija modula - eSavjetnik");

    const [selectedModule, setSelectedModule] = useState<OuterModule>();

    // filter values
    const [statusFilter, setStatusFilter] = useState<UserAccountActiveStatus[]>([]);
    const [sortBy, setSortBy] = useState<SortingRule<object>>();

    const [search, setSearch] = useState<string>("");
    const debouncedSearch = useDebounced(search, INPUT_DEBOUNCE_TIME);

    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearch]);

    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);

    // modal state
    const [isModalOpen, setIsModalOpen] = useState(false);

    // dropdown states
    const [showStatusFilter, setShowStatusFilter] = useState(false);

    // refs
    const statusFilterRef = useRef<HTMLDivElement>(null);

    // outside click
    const handleStatusFilterOutsideClick = () => setShowStatusFilter(false);
    useOutsideClick(statusFilterRef, handleStatusFilterOutsideClick);

    const { data: outerModules, isLoading } = useOuterModules(
        {
            search: debouncedSearch,
            isActive: statusFilter.length > 0 ? (statusFilter[0] === UserAccountActiveStatus.Inactive ? false : true) : null,
            sortField: sortBy?.id,
            descending: sortBy?.desc,
            page: currentPage,
            pageSize: parseInt(pageSize.value),
            isPublic: false,
        },
        true
    );

    // handle filter change
    const handleStatusFilterChange = (status: UserAccountActiveStatus) =>
        handleUpdateFilterPreferences(STATUS_FILTER_NAME, status);

    // filter clear
    const handleStatusFilterClear = () => {
        setSearch("");
        handleUpdateFilterPreferences(STATUS_FILTER_NAME, undefined, true);
    };

    // handle click
    const handleStatusFilterClick = (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
        setShowStatusFilter(prev => !prev);

    const handleRowSelect = (row: OuterModule) => {
        setSelectedModule(row);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedModule(undefined);
    };

    const handleUpdateFilterPreferences = (
        filterName: string,
        filterValue?: number,
        clearFilter?: boolean
    ) => {
        updateFilterPreferencesMutation.mutate({
            filterName,
            filterIntValue: filterValue,
            clearFilter: clearFilter,
        });
    };

    const filterPreferencesQuery = useGetFilterPreferencesQuery([STATUS_FILTER_NAME]);

    // query
    const updateFilterPreferencesMutation = useUpdateFilterPreferences();

    useEffect(() => {
        if (filterPreferencesQuery.some(s => s.isLoading)) return;

        const statusFilterData = filterPreferencesQuery.find(
            fp => fp.data?.filterName === STATUS_FILTER_NAME
        )?.data?.filterIntValues;

        if (statusFilterData) setStatusFilter(statusFilterData);
    }, [filterPreferencesQuery]);

    useOutsideClick(statusFilterRef, handleStatusFilterOutsideClick);

     const { lowVision } = useContext(AccessibilityContext);

    return (
        <>
            <div className="modules-administration">
                <div className="f-c g-24 even">
                    <div className="f-r g-24 even">
                        <HeaderFilter title="DODATNI MODULI">
                            <FilterGroup>
                                <div ref={statusFilterRef}>
                                    <StatusFilter
                                        onChange={handleStatusFilterChange}
                                        onClear={handleStatusFilterClear}
                                        onClick={handleStatusFilterClick}
                                        value={statusFilter}
                                        isActive={statusFilter.length !== 0}
                                        showDropdown={showStatusFilter}
                                    />
                                </div>

                                <FilterClear
                                    show={Boolean(statusFilter.length || search.length)}
                                    onClearFilter={handleStatusFilterClear}
                                />
                            </FilterGroup>
                        </HeaderFilter>

                        <div className="f-r g-24 even">
                            <div className="f-r g-24 even">
                                <Button
                                    size="xsmall"
                                    className="module-administration-button"
                                    variant="primary-ghost"
                        lowVision={lowVision}
                                >
                                    <Link to={AppRoutes.ModuleVersionsAdministration}>
                                        POVIJEST IZMJENA
                                    </Link>
                                </Button>

                                <Button
                                    size="small"
                                    className="module-administration-button"
                                    variant="primary"
                    lowVision={lowVision}
                                    onClick={() => setIsModalOpen(true)}
                                >
                                    KREIRAJ NOVI
                                </Button>
                            </div>

                            <Input
                                id="modules-administration-search"
                                name="modules-administration-search"
                                type="text"
                                size="medium"
                                placeholder="Pretraži module."
                                onChange={e => setSearch(e.target.value)}
                                value={search}
                                leftIcon={<SearchIcon className="search-icon" />}
                            />
                        </div>
                    </div>

                    <OuterModulesTable
                        outerModules={outerModules}
                        isLoading={isLoading}
                        setSortBy={setSortBy}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        onRowSelect={handleRowSelect}
                    />
                </div>
            </div>

            <OuterModuleDetailsModal
                module={selectedModule}
                totalModules={outerModules?.total ?? 0}
                isOpen={isModalOpen}
                handleClose={closeModal}
            />
        </>
    );
};
